/**
 * sliceGridOverlay.js — [12.0.15] СЕТКА СРЕЗОВ СВОИМИ РУКАМИ.
 *
 * ЗАЧЕМ СВОЁ. Библиотечный OverlayGridTool молчалив: на каждом несоблюдённом
 * условии он просто не рисует, а при неполных метаданных падает внутри
 * (`Cannot read properties of undefined (reading '0')` — на срезе, у которого
 * провайдер не отдал rowCosines/columnCosines). Одно падение убивает создание
 * ВСЕЙ разметки, и линий нет нигде. Проверить его можно было только глазами
 * владельца — каждая проверка стоила ему круга.
 *
 * Здесь та же задача решается обычной арифметикой: плоскость среза пересекается
 * с плоскостью окна по отрезку, отрезок переводится в координаты холста и
 * рисуется в СВОЁМ слое SVG. Всё, кроме последнего шага, — чистые функции без
 * DOM: они проверяются числами заранее (tests/verify_12_0_15.mjs), включая
 * координаты линий на холсте.
 *
 * ЧТО РИСУЕТСЯ. На поперечном виде (боковом, фронтальном) — линии, по которым
 * проходят срезы серии: видно охват съёмки и положение каждого среза. На
 * аксиальном виде линий нет и быть не может: там срезы параллельны экрану.
 */

const КЛАСС_СЛОЯ = 'mkh-grid-layer';

/**
 * Разобрать геометрию среза из imagePlaneModule.
 *
 * [12.0.15] ОТСЕВ НЕГОДНЫХ — первая половина задачи. Метаданные приходят
 * неполными чаще, чем кажется: rowCosines/columnCosines в DICOM отдельными
 * тегами не лежат, они выводятся из ImageOrientationPatient, и провайдер
 * заполняет их не на всех путях загрузки. Поэтому здесь принимается и то и
 * другое, а срез без обязательных полей отбраковывается — молча для картинки,
 * но со счётом наружу, чтобы было видно, сколько выпало.
 *
 * @param {object} pm — imagePlaneModule
 * @returns {{ipp:number[], r:number[], c:number[], ш:number, в:number}|null}
 */
export function геометрияСреза(pm) {
  if (!pm) return null;
  const ipp = pm.imagePositionPatient;
  if (!Array.isArray(ipp) || ipp.length < 3 || !ipp.every(Number.isFinite)) return null;

  let r = pm.rowCosines, c = pm.columnCosines;
  if (!Array.isArray(r) || !Array.isArray(c) || r.length < 3 || c.length < 3) {
    const iop = pm.imageOrientationPatient;
    if (Array.isArray(iop) && iop.length >= 6) { r = iop.slice(0, 3); c = iop.slice(3, 6); }
    else return null;
  }
  r = r.map(Number); c = c.map(Number);
  if (!r.every(Number.isFinite) || !c.every(Number.isFinite)) return null;

  const столбцов = Number(pm.columns), строк = Number(pm.rows);
  const шагX = Number(pm.columnPixelSpacing ?? pm.pixelSpacing?.[1]);
  const шагY = Number(pm.rowPixelSpacing ?? pm.pixelSpacing?.[0]);
  if (!Number.isFinite(столбцов) || !Number.isFinite(строк) || столбцов <= 0 || строк <= 0) return null;
  if (!Number.isFinite(шагX) || !Number.isFinite(шагY) || шагX <= 0 || шагY <= 0) return null;

  return { ipp: ipp.map(Number), r, c, ш: столбцов * шагX, в: строк * шагY };
}

/** Четыре угла среза в координатах пациента: левый верх, правый верх, левый низ, правый низ. */
export function углыСреза(г) {
  const { ipp, r, c, ш, в } = г;
  const лв = [...ipp];
  const пв = [ipp[0] + r[0] * ш, ipp[1] + r[1] * ш, ipp[2] + r[2] * ш];
  const лн = [ipp[0] + c[0] * в, ipp[1] + c[1] * в, ipp[2] + c[2] * в];
  const пн = [пв[0] + c[0] * в, пв[1] + c[1] * в, пв[2] + c[2] * в];
  return [лв, пв, лн, пн];
}

/**
 * Отрезок, по которому плоскость среза пересекает плоскость окна.
 *
 * Считаем пересечение плоскости окна со ВСЕМИ ЧЕТЫРЬМЯ рёбрами прямоугольника
 * среза и берём две крайние точки. Это надёжнее библиотечного приёма (там
 * выбирается одна из двух заранее заготовленных пар рёбер по признаку
 * перпендикулярности): при косой съёмке пересечение приходится на другую пару,
 * и заготовка промахивается.
 *
 * @returns {[number[], number[]]|null} — null, если плоскости параллельны или
 *   срез не задевает плоскость окна
 */
export function отрезокНаПлоскости(углы, нормальОкна, точкаОкна) {
  const [лв, пв, лн, пн] = углы;
  const n = нормальОкна;
  const d = n[0] * точкаОкна[0] + n[1] * точкаОкна[1] + n[2] * точкаОкна[2];
  const рёбра = [[лв, пв], [пв, пн], [пн, лн], [лн, лв]];
  const точки = [];
  for (const [A, B] of рёбра) {
    const nA = n[0] * A[0] + n[1] * A[1] + n[2] * A[2];
    const AB = [B[0] - A[0], B[1] - A[1], B[2] - A[2]];
    const знам = n[0] * AB[0] + n[1] * AB[1] + n[2] * AB[2];
    if (Math.abs(знам) < 1e-9) continue;              // ребро лежит в плоскости или параллельно
    const t = (d - nA) / знам;
    if (t < -1e-6 || t > 1 + 1e-6) continue;          // пересечение вне ребра
    точки.push([A[0] + AB[0] * t, A[1] + AB[1] * t, A[2] + AB[2] * t]);
  }
  if (точки.length < 2) return null;
  // Две самые далёкие друг от друга точки — концы отрезка (углы дают дубли).
  let a = точки[0], b = точки[1], лучш = -1;
  for (let i = 0; i < точки.length; i++) {
    for (let j = i + 1; j < точки.length; j++) {
      const д = (точки[i][0] - точки[j][0]) ** 2 + (точки[i][1] - точки[j][1]) ** 2 + (точки[i][2] - точки[j][2]) ** 2;
      if (д > лучш) { лучш = д; a = точки[i]; b = точки[j]; }
    }
  }
  return лучш > 1e-6 ? [a, b] : null;
}

/** Нормаль плоскости среза (единичная) — векторное произведение косинусов. */
export function нормальСреза(г) {
  const { r, c } = г;
  const n = [r[1] * c[2] - r[2] * c[1], r[2] * c[0] - r[0] * c[2], r[0] * c[1] - r[1] * c[0]];
  const д = Math.hypot(n[0], n[1], n[2]);
  return д > 1e-9 ? [n[0] / д, n[1] / д, n[2] / д] : null;
}

/**
 * Подготовить источник: геометрия годных срезов, прорежённая до предела.
 *
 * Прорежаем ПОСЛЕ отсева: иначе на негодные срезы уходили бы места в
 * двадцати четырёх, и линий выходило бы меньше без всякой причины.
 *
 * @param {string[]} imageIds
 * @param {(id: string) => object} взятьПлоскость — metaData.get('imagePlaneModule', id)
 * @param {number} предел
 * @returns {{срезы: object[], годных: number, отброшено: number}}
 */
export function подготовитьИсточник(imageIds, взятьПлоскость, предел = 24) {
  const годные = [];
  let отброшено = 0;
  for (const id of imageIds || []) {
    let г = null;
    try { г = геометрияСреза(взятьПлоскость(id)); } catch (e) { г = null; }
    if (г) годные.push(г); else отброшено++;
  }
  let срезы = годные;
  if (годные.length > предел) {
    const шаг = годные.length / предел;
    срезы = [];
    for (let i = 0; i < предел; i++) срезы.push(годные[Math.floor(i * шаг)]);
  }
  return { срезы, годных: годные.length, отброшено };
}

/**
 * Посчитать линии для одного окна — в координатах ХОЛСТА.
 *
 * Отдельно от рисования, чтобы проверять числами: на вход геометрия срезов,
 * плоскость окна и перевод «мир → холст», на выходе готовые отрезки.
 *
 * @param {object[]} срезы — геометрии из подготовитьИсточник
 * @param {{нормаль:number[], точка:number[], вМир:Function}} окно
 * @returns {{x1:number,y1:number,x2:number,y2:number}[]}
 */
export function линииДляОкна(срезы, окно) {
  const итог = [];
  if (!окно?.нормаль || !окно?.точка || typeof окно.вМир !== 'function') return итог;
  for (const г of срезы || []) {
    const n = нормальСреза(г);
    // Параллельные плоскости не пересекаются: на аксиальном виде линий нет.
    if (!n || Math.abs(n[0] * окно.нормаль[0] + n[1] * окно.нормаль[1] + n[2] * окно.нормаль[2]) > 0.999) continue;
    const отрезок = отрезокНаПлоскости(углыСреза(г), окно.нормаль, окно.точка);
    if (!отрезок) continue;
    const [p1, p2] = отрезок.map((т) => окно.вМир(т));
    if (!p1 || !p2 || !Number.isFinite(p1[0]) || !Number.isFinite(p2[0])) continue;
    итог.push({ x1: p1[0], y1: p1[1], x2: p2[0], y2: p2[1] });
  }
  return итог;
}

// ── Слой на экране ───────────────────────────────────────────────────────
// Ниже — единственная часть, которой нужен DOM. Всё, что можно было посчитать
// без него, посчитано выше.

/** Найти или создать свой слой поверх холста окна. */
function _слой(элемент) {
  let слой = элемент.querySelector(`:scope > .${КЛАСС_СЛОЯ}`);
  if (!слой) {
    слой = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    слой.setAttribute('class', КЛАСС_СЛОЯ);
    слой.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;'
      + 'pointer-events:none;overflow:visible;z-index:5';
    элемент.appendChild(слой);
  }
  return слой;
}

/** Убрать свой слой из окна. */
function _снятьСлой(элемент) {
  элемент?.querySelector(`:scope > .${КЛАСС_СЛОЯ}`)?.remove();
}

let _включена = null;   // {окна: Map<vpId, {элемент, обработчик}>, срезы, цвет}

/**
 * Нарисовать сетку в одном окне. Возвращает число нарисованных линий —
 * не намерение, а результат: столько узлов легло в слой.
 */
export function нарисоватьВОкне(vp, срезы, цвет = 'rgba(120,190,255,0.85)') {
  const элемент = vp?.element;
  if (!элемент) return 0;
  const камера = vp.getCamera?.();
  if (!камера?.viewPlaneNormal || !камера?.focalPoint) return 0;

  const линии = линииДляОкна(срезы, {
    нормаль: [...камера.viewPlaneNormal],
    точка: [...камера.focalPoint],
    вМир: (т) => vp.worldToCanvas(т),
  });

  const слой = _слой(элемент);
  слой.innerHTML = '';
  for (const л of линии) {
    const у = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    у.setAttribute('x1', String(л.x1)); у.setAttribute('y1', String(л.y1));
    у.setAttribute('x2', String(л.x2)); у.setAttribute('y2', String(л.y2));
    у.setAttribute('stroke', цвет);
    у.setAttribute('stroke-width', '1');
    у.setAttribute('data-mkh', 'сетка-срезов');
    слой.appendChild(у);
  }
  return линии.length;
}

/**
 * Включить сетку: нарисовать во всех окнах и держать её при прокрутке и
 * повороте камеры. Перерисовка по событию отрисовки окна — иначе линии
 * «отклеиваются» от снимка при первом же движении.
 *
 * @returns {{ok: boolean, окон: number, линий: number, отброшено: number, why?: string}}
 */
export function включитьСеткуСрезов(окна, imageIds, взятьПлоскость, предел = 24) {
  выключитьСеткуСрезов();
  const { срезы, годных, отброшено } = подготовитьИсточник(imageIds, взятьПлоскость, предел);
  if (!срезы.length) {
    return { ok: false, окон: 0, линий: 0, отброшено, why: 'ни у одного среза серии нет полной геометрии' };
  }

  const состояние = { окна: new Map(), срезы };
  let всегоЛиний = 0, сОкнами = 0;
  for (const vp of окна || []) {
    if (!vp?.element) continue;
    const линий = нарисоватьВОкне(vp, срезы);
    всегоЛиний += линий;
    if (линий) сОкнами++;
    const обработчик = () => { try { нарисоватьВОкне(vp, срезы); } catch (e) { /* окно уже снято */ } };
    vp.element.addEventListener('CORNERSTONE_IMAGE_RENDERED', обработчик);
    vp.element.addEventListener('CORNERSTONE_CAMERA_MODIFIED', обработчик);
    состояние.окна.set(vp.id, { элемент: vp.element, обработчик });
  }
  _включена = состояние;
  return {
    ok: всегоЛиний > 0,
    окон: сОкнами,
    линий: всегоЛиний,
    отброшено,
    годных,
    why: всегоЛиний ? undefined : 'ни в одном окне линии не легли — все виды параллельны срезам (включите MPR)',
  };
}

/** Выключить сетку: снять слои и обработчики. */
export function выключитьСеткуСрезов() {
  if (!_включена) return false;
  for (const [, { элемент, обработчик }] of _включена.окна) {
    try {
      элемент.removeEventListener('CORNERSTONE_IMAGE_RENDERED', обработчик);
      элемент.removeEventListener('CORNERSTONE_CAMERA_MODIFIED', обработчик);
      _снятьСлой(элемент);
    } catch (e) { /* окно уже снято вместе с раскладкой */ }
  }
  _включена = null;
  return true;
}

/** Включена ли сейчас. */
export function сеткаВключена() { return !!_включена; }

/** Сколько линий сейчас лежит в слоях — считается по DOM, а не по намерениям. */
export function линийНаЭкране() {
  if (!_включена) return 0;
  let n = 0;
  for (const [, { элемент }] of _включена.окна) {
    try { n += элемент.querySelectorAll(`.${КЛАСС_СЛОЯ} line`).length; } catch (e) { /* окно снято */ }
  }
  return n;
}
